import { Fragment, useMemo } from 'react'
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import {
  faChevronRight,
  faReceipt,
  faWarehouse,
} from '@fortawesome/pro-solid-svg-icons'

import { Card, CardBody, Heading } from 'Components/_theme'
import { Icon } from 'Components/Icon/Icon'
import { RentalStatusBadge } from 'Components/RentalStatusBadge/RentalStatusBadge'
import { RentalStatus } from 'Components/RentalStatusBadge/RentalStatusBadge.types'

import { dashToUnderscore } from 'Utilities'

import { ORDER_STATUS_TRANSLATION_IDS } from 'Constants/orderStatus'
import { PORTAL_CONTENT_MODEL } from 'Constants/portalContentModel'

import { ContentTypeEnum } from 'Types/content/content.types'

import {
  DashboardNavigationCardIconEnum,
  DashboardNavigationCardProps,
} from './DashboardNavigationCard.types'

export const DashboardNavigationCard = (
  props: DashboardNavigationCardProps,
) => {
  const {
    caseItems,
    e2eSelector,
    icon,
    rentals,
    stocklistItemCount,
    title,
    total,
    url,
  } = props
  const intl = useIntl()

  const newSmartphoneBadge: boolean | undefined = rentals?.some(
    rentals => rentals.isUpgradePending === false && rentals.isUpgradeEligible,
  )

  const upgradeSmartphoneBadge: boolean | undefined = rentals?.some(
    rentals =>
      rentals.isUpgradePending === false && rentals.isEarlyUpgradeEligible,
  )

  const renderCardIcon = useMemo(() => {
    switch (icon) {
      case DashboardNavigationCardIconEnum.Rentals:
        return PORTAL_CONTENT_MODEL[ContentTypeEnum.rentals].icon
      case DashboardNavigationCardIconEnum.Employees:
        return PORTAL_CONTENT_MODEL[ContentTypeEnum.employees].icon
      case DashboardNavigationCardIconEnum.Depots:
        return faWarehouse
      case DashboardNavigationCardIconEnum.CaseItems:
        return PORTAL_CONTENT_MODEL[ContentTypeEnum.caseitems].icon
      case DashboardNavigationCardIconEnum.PhonePlans:
        return PORTAL_CONTENT_MODEL[ContentTypeEnum.phone_plans].icon
      case DashboardNavigationCardIconEnum.Invoices:
        return faReceipt
      default:
        return null
    }
  }, [icon])

  return (
    <Link
      key={url}
      className="w-100"
      data-e2e={e2eSelector}
      title={intl.formatMessage({ id: title })}
      to={url}
    >
      <Card className="NavigationCard mb-6">
        <CardBody>
          <div className="d-flex">
            <div className="w-100">
              <div className="d-flex">
                <div className="d-flex me-4 w-100">
                  <Heading as={5} size={2}>
                    <FormattedMessage id={title} />
                  </Heading>

                  <div className="d-flex flex-column flex-sm-row justify-content-end ms-auto">
                    <div className="d-flex justify-content-end">
                      {!!newSmartphoneBadge && (
                        <RentalStatusBadge
                          className="me-1 my-0"
                          status={RentalStatus.new_device}
                        />
                      )}
                    </div>

                    <div>
                      {!!upgradeSmartphoneBadge && (
                        <RentalStatusBadge
                          className="mx-sm-1 my-0"
                          status={RentalStatus.upgrade}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center mt-2">
                {renderCardIcon && (
                  <Icon className="me-2" icon={renderCardIcon} size={14} />
                )}

                <span>
                  <FormattedMessage id="Total" />: {total}
                  {!!stocklistItemCount && (
                    <span className="ms-2 small text-wrap">
                      <FormattedPlural
                        one={intl.formatMessage({
                          id: 'Device',
                        })}
                        other={intl.formatMessage({
                          id: 'Devices',
                        })}
                        value={stocklistItemCount}
                      />
                      : {stocklistItemCount}
                    </span>
                  )}
                  {!!caseItems && !!caseItems.countByStatus && (
                    <span className="ms-2 small text-wrap">
                      {caseItems.countByStatus.map(countByStatus => (
                        <Fragment key={countByStatus.status}>
                          <FormattedMessage
                            id={
                              ORDER_STATUS_TRANSLATION_IDS[
                                dashToUnderscore(countByStatus.status)
                              ]
                            }
                          />
                          <span className="me-2">: {countByStatus.count}</span>
                        </Fragment>
                      ))}
                    </span>
                  )}
                </span>
              </div>
            </div>

            <Icon icon={faChevronRight} size={16} />
          </div>
        </CardBody>
      </Card>
    </Link>
  )
}
